import Button from '@atoms/Button/Button';
import Tag, { TagVariant } from '@atoms/Tag/Tag';
import SiteEnum from '@models/enums/SiteEnum';
import { QueryState, SelectedFilter } from '@models/IListingWrapper';
import { fadeIn } from '@utility/config/animations';
import {
  useListingDispatch,
  useListingState,
} from '@utility/context/ListingContext';
import { ActionType } from '@utility/reducers/ListingWrapper';
import { motion } from 'motion/react';
import { MouseEvent } from 'react';
import { UseFormSetValue } from 'react-hook-form';

interface Props {
  readonly clearAllLabel: string;
  readonly displayedSelectedFilters: SelectedFilter[];
  readonly listingFilterParam?: string;
  readonly queryState: QueryState;
  readonly site: SiteEnum;
  readonly onClearAll: (
    e: MouseEvent<HTMLButtonElement>,
    params?: Partial<QueryState>
  ) => void;
  readonly setCleanQueryParams: (params: QueryState) => void;
  readonly setValue: UseFormSetValue<QueryState>;
  readonly updateSelectedInspiration: (updatedIds: string[]) => void;
}

const SelectedFiltersBar = ({
  clearAllLabel,
  displayedSelectedFilters,
  listingFilterParam,
  queryState,
  site,
  onClearAll,
  setCleanQueryParams,
  setValue,
  updateSelectedInspiration,
}: Props) => {
  const { sortedListingFilters, promoCardReferences, selectedFilters } =
    useListingState();
  const dispatch = useListingDispatch();

  if (!displayedSelectedFilters?.length) return null;

  const handleTagRemove = (id: string, key: string) => {
    const updatedFilters = selectedFilters.filter((filter) => filter.id !== id);
    dispatch({ type: ActionType.SELECTED_FILTERS, payload: updatedFilters });

    //Check if tag is a top level filter
    const indexOfListingFilter = sortedListingFilters
      .map((listingFilter) => listingFilter.filter.id)
      .indexOf(id);

    // If tag is top level filter then remove it from list of listing filters
    if (indexOfListingFilter > -1) {
      const newSortedListingFilters = [...sortedListingFilters];
      newSortedListingFilters.splice(indexOfListingFilter);
      dispatch({
        type: ActionType.SORTED_LISTING_FILTERS,
        payload: newSortedListingFilters,
      });
    }

    const params = () => {
      const currentValue = ([] as any[]).concat(
        queryState[key as keyof typeof queryState] || []
      );

      if (key === 'freetext') {
        return { freetext: '' };
      }

      return {
        [key]: currentValue.filter((item) => item !== id),
      };
    };

    dispatch({ type: ActionType.CLEAR_SEARCH, payload: 'increment' });

    // component
    const removedFilter = selectedFilters.find((f) => f.id === id);
    if (removedFilter?.promoCardReference) {
      const updatedRefs = promoCardReferences.filter(
        (ref) => ref !== removedFilter.promoCardReference
      );
      const newValue = updatedRefs.length > 0 ? updatedRefs[0] : 0;

      dispatch({
        type: ActionType.PROMO_CARD_REFERENCES,
        payload: updatedRefs,
      });

      setValue('promoCardReference', newValue);
      setCleanQueryParams({
        ...queryState,
        ...params(),
        page: 0,
        skip: 0,
        promoCardReference: newValue,
      });
    } else {
      const newQueryState = {
        ...queryState,
        ...params(),
        page: 0,
        skip: 0,
        promoCardReference: 0,
      };
      setCleanQueryParams(newQueryState);

      // If the removed tag is a listing filter (not e.g. freetext), update the inspiration image
      if (key === listingFilterParam) {
        const updatedIds =
          ((newQueryState as Record<string, unknown>)[listingFilterParam] as
            | string[]
            | undefined) ?? [];

        updateSelectedInspiration(updatedIds);
      }
    }
  };

  return (
    <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2 md:tw-gap-3">
      {displayedSelectedFilters.map(({ name, label, id }, index) => (
        <motion.div
          key={`${id}-${index}`}
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <Tag
            label={label}
            onClick={() => handleTagRemove(id, name)}
            icon
            largeText
            variant={
              site === SiteEnum.B2B ? TagVariant.Yellow : TagVariant.Grey
            }
          />
        </motion.div>
      ))}

      <motion.div
        key="clear"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <Button
          variant="link"
          buttonClasses="tw-py-[2px] !tw-px-2"
          label={clearAllLabel}
          onClick={(e) => onClearAll(e, {})}
        />
      </motion.div>
    </div>
  );
};

export default SelectedFiltersBar;
